/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Slider from '../../components/slider/Slider';
import TextField from '../../components/textfield/TextField';
import {dlsColor} from '../../theme/theme';
import {useGeofenceCardStyles} from './geofenceCardStyles';

// defines the radius control's values
export type GeofenceRadius = {
  value: number;
  max: number;
  step: number;
  marks: Array<{value: number, label: string}>;
  unitLabel: string;
  onSlide: (value: number) => void;
  onInputChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

export type GeofenceRadiusControlProps = {
  radius: GeofenceRadius;
  className?: string;
};

const useStyles = makeStyles(() => ({
  radius : {
    display       : 'flex',
    flexDirection : 'column',
    gap           : 8
  },
  radiusHeader : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'flex-end',
    gap            : 8
  },
  // the shared current-theme TextField is a 44px, 8px 16px padded box -
  // the radius field is the design's smaller 32px, 4px 8px box ...
  radiusInput : {
    width                  : 88,
    '& .MuiInputBase-root' : {
      height  : '32px !important',
      padding : '4px 8px !important'
    },
    '& .MuiInputBase-input' : {
      padding    : '0 !important',
      fontFamily : 'Inter',
      fontSize   : '16px !important',
      lineHeight : '22px !important',
      color      : dlsColor('grey1')
    },
    // the shared TextField greys its background, border and text on hover -
    // the radius box keeps its resting look instead ...
    '& .MuiInputBase-root:hover' : {
      background : `${dlsColor('white')} !important`
    },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline' : {
      borderColor : `${dlsColor('grey5')} !important`
    },
    '& .MuiInputBase-root:hover .MuiInputBase-input' : {
      color : `${dlsColor('grey1')} !important`
    }
  },
  radiusUnit : {
    paddingLeft : 4,
    fontFamily  : 'Inter',
    fontSize    : '16px',
    lineHeight  : '22px',
    color       : dlsColor('grey1')
  },
  // the shared Slider's own full-width mode leaves 12px free at its right
  // edge (kept for the Map Layers opacity sliders) - the card's radius
  // slider spans the whole row instead, scoped to this control only ...
  radiusSlider : {
    '& .MuiSlider-root' : {
      width : '100% !important'
    },
    '& .MuiSlider-mark' : {
      display : 'none'
    },
    '& .MuiSlider-markLabel' : {
      fontFamily    : 'Inter',
      fontWeight    : 500,
      fontSize      : '10px',
      lineHeight    : '16px',
      letterSpacing : '1px',
      textTransform : 'uppercase',
      color         : dlsColor('grey1')
    }
  }
}));

/**
 * Renders the current theme's geofence card radius control - its labelled
 * radius field and the radius slider below it.
 *
 * ```typescript
 *
 *   import {GeofenceRadiusControl} from
 *     '@ion/dls/src/maps/geofences/GeofenceRadiusControl';
 *   ...
 *
 *   <GeofenceRadiusControl radius={radius} />
 *
 * ```
 *
 * @param   radius    The radius control's values.
 * @param   className An optional class for the control's root.
 * @returns           The radius control.
 */
export const GeofenceRadiusControl = ({radius, className}: GeofenceRadiusControlProps) => {
  const cardClasses = useGeofenceCardStyles();
  const classes     = useStyles();

  const onRadiusSlide = function(event, value: number | number[]) {
    radius.onSlide(value as number);
  };

  return (
    <div className={classnames(classes.radius, className)}>
      <div className={classes.radiusHeader}>
        <span className={cardClasses.label}>
          {translate('maps:geofences.radius')}
        </span>
        <TextField className={classes.radiusInput}
          value={radius.value}
          onChange={radius.onInputChange}
          InputProps={{
            endAdornment : (
              <span className={classes.radiusUnit}>
                {radius.unitLabel}
              </span>
            )
          }}
          inputProps={{maxLength: 7}} />
      </div>
      <div className={classes.radiusSlider}>
        <Slider value={radius.value}
          step={radius.step}
          max={radius.max}
          marks={radius.marks}
          onSlide={onRadiusSlide} />
      </div>
    </div>
  );
};

export default GeofenceRadiusControl;
